/**
 * Indeterminate progress bar shown while a chat/call is connecting.
 *
 * Web expresses the sweep as a CSS keyframe (`indeterminate-progress` in the
 * widget's tailwind config): a one-third-width fill that starts off the left
 * edge, catches up at 40% of the cycle, then overshoots the right edge, all
 * while stretching from 0.4x to 0.6x and back. RN has no keyframes, so the same
 * three stops are driven by a single `Animated.Value` and read back as a
 * translate + scale — both transform properties, so the whole thing runs on the
 * native driver and survives a busy JS thread.
 *
 * CSS can say `translateX(-100%)` and mean "the fill's own width"; RN transforms
 * are absolute dp, which is why the track measures itself with `onLayout` and
 * the travel distances are multiples of the measured fill width.
 */

import { useEffect, useRef, useState, type FC } from "react";
import { Animated, Easing, StyleSheet, View, type LayoutChangeEvent, type StyleProp, type ViewStyle } from "react-native";

import { GradientFill, solidColor } from "../lib/gradient";
import { ringgId } from "../lib/testing";
import { useWidgetTheme } from "../lib/widget-theme";

interface LoadingBarProps {
  /** Accessible label (not rendered visually). */
  ariaLabel?: string;
  /** Web's `className` slot: layout overrides from the parent. */
  style?: StyleProp<ViewStyle>;
}

/** One full sweep — the `1.4s` of the CSS animation shorthand. */
const SWEEP_DURATION_MS = 1400;

/** The middle keyframe, where the fill has caught up with the track's left edge. */
const MIDPOINT = 0.4;

const KEYFRAMES = [0, MIDPOINT, 1];

/** `translateX` per keyframe, as a multiple of the fill's own width. */
const TRAVEL_RATIOS = [-1, 0, 2.5];

/** `scaleX` per keyframe. */
const STRETCH_SCALES = [0.4, 0.6, 0.4];

/** `cubic-bezier(0.65, 0, 0.35, 1)`. */
const SEGMENT_EASING = Easing.bezier(0.65, 0, 0.35, 1);

/**
 * A CSS `animation-timing-function` applies to EVERY keyframe interval, so the
 * sweep eases twice: once into the midpoint, once out of it. Expressing that as
 * `Animated.sequence` of two timings would reproduce the curve but hand the
 * seam between them back to the JS thread — `sequence` starts each child from
 * JS, so the bar hitches exactly while the app is busy connecting.
 *
 * Folding both segments into one easing keeps it a single native animation.
 * RN samples the easing into a frame table when the native driver is on, so an
 * arbitrary function like this one costs nothing at run time.
 */
const SWEEP_EASING = (t: number): number => (t < MIDPOINT ? MIDPOINT * SEGMENT_EASING(t / MIDPOINT) : MIDPOINT + (1 - MIDPOINT) * SEGMENT_EASING((t - MIDPOINT) / (1 - MIDPOINT)));

/** `w-1/3` of the track. */
const FILL_WIDTH_RATIO = 1 / 3;

const ROW_HEIGHT = 48;
const TRACK_HEIGHT = 6;

/** `rounded-full`; RN clamps to half the shorter side, so any large value is a pill. */
const PILL_RADIUS = 9999;

const LoadingBar: FC<LoadingBarProps> = ({ ariaLabel = "Connecting", style }) => {
  const theme = useWidgetTheme();
  const [trackWidth, setTrackWidth] = useState(0);
  const progress = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    // One timing, looped: both eased segments live in SWEEP_EASING, so nothing
    // hands control back to JS between them.
    const sweep = Animated.loop(Animated.timing(progress, { toValue: 1, duration: SWEEP_DURATION_MS, easing: SWEEP_EASING, useNativeDriver: true }));
    sweep.start();
    return () => sweep.stop();
  }, [progress]);

  const onTrackLayout = (event: LayoutChangeEvent) => setTrackWidth(event.nativeEvent.layout.width);

  // Zero until the first layout lands; the fill is simply invisible for that frame.
  const fillWidth = trackWidth * FILL_WIDTH_RATIO;
  const translateX = progress.interpolate({ inputRange: KEYFRAMES, outputRange: TRAVEL_RATIOS.map((ratio) => ratio * fillWidth) });
  const scaleX = progress.interpolate({ inputRange: KEYFRAMES, outputRange: STRETCH_SCALES });

  return (
    <View testID={ringgId("loading-bar")} style={[styles.container, style]}>
      <View
        testID={ringgId("loading-bar-track")}
        accessibilityRole="progressbar"
        accessibilityLabel={ariaLabel}
        // `aria-busy` — an indeterminate bar has no value to announce.
        accessibilityState={{ busy: true }}
        onLayout={onTrackLayout}
        style={[styles.track, { backgroundColor: solidColor(theme.borderColor) }]}
      >
        <Animated.View testID={ringgId("loading-bar-fill")} style={[styles.fill, { width: fillWidth, transform: [{ translateX }, { scaleX }] }]}>
          <GradientFill color={theme.primaryColor} style={styles.fillSurface} />
        </Animated.View>
      </View>
    </View>
  );
};

export default LoadingBar;

const styles = StyleSheet.create({
  container: { height: ROW_HEIGHT, flexDirection: "row", alignItems: "center", justifyContent: "center" },
  track: { height: TRACK_HEIGHT, width: "90%", borderRadius: PILL_RADIUS, overflow: "hidden" },
  fill: { position: "absolute", top: 0, bottom: 0, left: 0, borderRadius: PILL_RADIUS },
  fillSurface: { ...StyleSheet.absoluteFill, borderRadius: PILL_RADIUS },
});
